/**
 * Alignment Guidelines Utilities
 *
 * Pure functions for detecting when players are aligned on the same
 * grid coordinates (same X or Y after rounding).
 */

import { LINE_OF_SCRIMMAGE, PLAYER_RADIUS_FEET } from '@/constants'

// Minimum distance between player edges to show a distance label
const MIN_LABEL_DISTANCE_FEET = 2

/** Player position with grid coordinates */
export interface GridPlayer {
	id: string
	x: number
	y: number
}

/** Position reference in alignment results */
export interface PlayerReference {
	playerId: string
	position: { x: number; y: number }
}

export interface AlignmentGuideline {
	type: 'horizontal' | 'vertical'
	sourcePlayer: PlayerReference
	alignedPlayer: PlayerReference
	distanceFeet: number
}

export interface AlignmentSegment {
	type: 'horizontal' | 'vertical'
	startPlayer: PlayerReference
	endPlayer: PlayerReference
	distanceFeet: number
	showLabel: boolean
}

/** Round coordinate to nearest grid position */
function roundToGrid(value: number): number {
	return Math.round(value)
}

/** Create a PlayerReference from a GridPlayer */
function toPlayerReference(player: GridPlayer): PlayerReference {
	return {
		playerId: player.id,
		position: { x: player.x, y: player.y },
	}
}

/**
 * Find all players aligned with a given player.
 *
 * Alignment is determined by exact grid match:
 * - Vertical alignment: same X coordinate (after rounding)
 * - Horizontal alignment: same Y coordinate (after rounding)
 *
 * @param player - The source player to check alignments for
 * @param allPlayers - All other players to check against (should exclude source player)
 * @returns Array of alignment guidelines
 *
 * @example
 * const player = { id: 'p1', x: 80, y: 28 }
 * const others = [{ id: 'p2', x: 80, y: 40 }]
 * const aligned = findAlignedPlayers(player, others)
 * // => [{ type: 'vertical', sourcePlayer: {...}, alignedPlayer: {...}, distanceFeet: 12 }]
 */
export function findAlignedPlayers(
	player: GridPlayer,
	allPlayers: GridPlayer[],
): AlignmentGuideline[] {
	const guidelines: AlignmentGuideline[] = []
	const playerGridX = roundToGrid(player.x)
	const playerGridY = roundToGrid(player.y)

	for (const other of allPlayers) {
		if (other.id === player.id) continue

		const otherGridX = roundToGrid(other.x)
		const otherGridY = roundToGrid(other.y)

		// Horizontal alignment: same Y coordinate
		if (playerGridY === otherGridY) {
			const distance = Math.abs(other.x - player.x)
			if (distance > 0) {
				guidelines.push({
					type: 'horizontal',
					sourcePlayer: toPlayerReference(player),
					alignedPlayer: toPlayerReference(other),
					distanceFeet: distance,
				})
			}
		}

		// Vertical alignment: same X coordinate
		if (playerGridX === otherGridX) {
			const distance = Math.abs(other.y - player.y)
			if (distance > 0) {
				guidelines.push({
					type: 'vertical',
					sourcePlayer: toPlayerReference(player),
					alignedPlayer: toPlayerReference(other),
					distanceFeet: distance,
				})
			}
		}
	}

	return guidelines
}

/**
 * Create segments between adjacent players sorted along an axis.
 * @internal
 */
function createAdjacentSegments(
	players: GridPlayer[],
	axis: 'horizontal' | 'vertical',
	minLabelSpacing: number,
): AlignmentSegment[] {
	if (players.length < 2) return []

	// Sort by the appropriate coordinate
	const sorted = [...players].sort((a, b) =>
		axis === 'horizontal' ? a.x - b.x : a.y - b.y,
	)

	const segments: AlignmentSegment[] = []

	for (let i = 0; i < sorted.length - 1; i++) {
		const start = sorted[i]!
		const end = sorted[i + 1]!

		// Edge-to-edge distance (subtract both player radii)
		const centerDistance =
			axis === 'horizontal'
				? Math.abs(end.x - start.x)
				: Math.abs(end.y - start.y)
		const distance = Math.max(0, centerDistance - PLAYER_RADIUS_FEET * 2)

		segments.push({
			type: axis,
			startPlayer: toPlayerReference(start),
			endPlayer: toPlayerReference(end),
			distanceFeet: distance,
			showLabel: distance > MIN_LABEL_DISTANCE_FEET && distance >= minLabelSpacing,
		})
	}

	return segments
}

/**
 * Find alignment segments between a player and all aligned players.
 *
 * Unlike findAlignedPlayers which creates guidelines from source to each aligned player,
 * this creates segments between adjacent pairs of players on the same alignment line.
 * This is useful for showing distance labels only where there's space.
 *
 * @param player - The hovered/source player
 * @param allPlayers - All other players to check (should exclude source player)
 * @param minLabelSpacing - Minimum distance in feet required to show label (default 6)
 * @returns Array of alignment segments with showLabel flag
 *
 * @example
 * const player = { id: 'receiver', x: 80, y: 28 }
 * const others = [
 *   { id: 'lineman1', x: 90, y: 28 },
 *   { id: 'lineman2', x: 91, y: 28 }
 * ]
 * const segments = findAlignmentSegments(player, others)
 * // => [
 * //   { type: 'horizontal', startPlayer: receiver, endPlayer: lineman1, distanceFeet: 10, showLabel: true },
 * //   { type: 'horizontal', startPlayer: lineman1, endPlayer: lineman2, distanceFeet: 1, showLabel: false }
 * // ]
 */
export function findAlignmentSegments(
	player: GridPlayer,
	allPlayers: GridPlayer[],
	minLabelSpacing = 6,
): AlignmentSegment[] {
	const playerGridX = roundToGrid(player.x)
	const playerGridY = roundToGrid(player.y)

	// Group aligned players by axis
	const horizontallyAligned: GridPlayer[] = []
	const verticallyAligned: GridPlayer[] = []

	for (const other of allPlayers) {
		if (other.id === player.id) continue

		const otherGridX = roundToGrid(other.x)
		const otherGridY = roundToGrid(other.y)

		if (playerGridY === otherGridY) {
			horizontallyAligned.push(other)
		}
		if (playerGridX === otherGridX) {
			verticallyAligned.push(other)
		}
	}

	return [
		...createAdjacentSegments(
			[...horizontallyAligned, player],
			'horizontal',
			minLabelSpacing,
		),
		...createAdjacentSegments(
			[...verticallyAligned, player],
			'vertical',
			minLabelSpacing,
		),
	]
}

/** Virtual player ID representing the line of scrimmage */
const LOS_PLAYER_ID = 'los'

/**
 * Check if any player blocks the path between a player and the LOS.
 * @internal
 */
function hasPlayerBlockingLOS(
	player: GridPlayer,
	allPlayers: GridPlayer[],
	playerGridX: number,
	isOffense: boolean,
): boolean {
	return allPlayers.some((other) => {
		if (other.id === player.id) return false

		const otherGridX = roundToGrid(other.x)
		const otherGridY = roundToGrid(other.y)

		if (otherGridX !== playerGridX) return false

		// Check if other player is between this player and the LOS
		return isOffense
			? otherGridY > player.y && otherGridY < LINE_OF_SCRIMMAGE
			: otherGridY > LINE_OF_SCRIMMAGE && otherGridY < player.y
	})
}

/**
 * Create an alignment segment from a player to the line of scrimmage (LOS).
 *
 * Only creates a segment if there's no other player between the player and the LOS
 * at the same X coordinate.
 *
 * @param player - The player to measure distance from
 * @param allPlayers - All other players to check for blocking
 * @param minLabelSpacing - Minimum distance in feet required to show label (default 1)
 * @returns An alignment segment to LOS, or null if blocked by another player
 */
export function createLOSAlignmentSegment(
	player: GridPlayer,
	allPlayers: GridPlayer[],
	minLabelSpacing = 1,
): AlignmentSegment | null {
	const playerGridX = roundToGrid(player.x)
	const playerGridY = roundToGrid(player.y)

	// Skip if player is at the LOS
	if (playerGridY === LINE_OF_SCRIMMAGE) {
		return null
	}

	const isOffense = player.y < LINE_OF_SCRIMMAGE

	if (hasPlayerBlockingLOS(player, allPlayers, playerGridX, isOffense)) {
		return null
	}

	// Edge-to-edge distance (subtract one player radius since LOS is a line)
	const centerDistance = Math.abs(player.y - LINE_OF_SCRIMMAGE)
	const distance = Math.max(0, centerDistance - PLAYER_RADIUS_FEET)

	const playerRef = toPlayerReference(player)
	const losRef: PlayerReference = {
		playerId: LOS_PLAYER_ID,
		position: { x: player.x, y: LINE_OF_SCRIMMAGE },
	}

	return {
		type: 'vertical',
		startPlayer: isOffense ? playerRef : losRef,
		endPlayer: isOffense ? losRef : playerRef,
		distanceFeet: distance,
		showLabel: distance > MIN_LABEL_DISTANCE_FEET && distance >= minLabelSpacing,
	}
}
